import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { SceneContextMenuBase } from "@/components/ui/scene-context-menu";
import {
  MenuDivider,
  selectVisibleContextMenuActions,
} from "@/components/ui/tree/tree-context-menu";
import { ACTIONS } from "@/components/ui/tree/tree-context-menu/actions";
import { useAppSelector } from "@/store/store-hooks";

import { AdjustTrajectoryAction } from "@/components/ui/tree/tree-context-menu/actions/adjust-trajectory-action";
import { DeleteVideoRecordingAction } from "@/components/ui/tree/tree-context-menu/actions/delete-video-recording-action";
import { DownloadVideoWalkAction } from "@/components/ui/tree/tree-context-menu/actions/download-action";
import { EditNameAction } from "@/components/ui/tree/tree-context-menu/actions/edit-name-action";
import { MoveDataSessionAction } from "@/components/ui/tree/tree-context-menu/actions/move-data-session-action";
import {
  roundedCalculatePosition,
  selectAncestor,
} from "@faro-lotv/app-component-toolbox";
import { FaroMenuSeparator } from "@faro-lotv/flat-ui";
import {
  IElement,
  isIElementSectionDataSession,
} from "@faro-lotv/ielement-types";
import { useState, type JSX } from "react";
import { shallowEqual } from "react-redux";
import { Vector3 } from "three";

type PathContextMenu = {
  /** The position of the context menu in three.js world coordinates */
  position: Vector3;

  /** The path to show the context menu for */
  path: IElement;

  /** Callback when the menu should be closed */
  onClose(): void;
};

/**
 * @returns A context menu for paths
 */
export function PathContextMenu({
  position,
  path,
  onClose,
}: PathContextMenu): JSX.Element | null {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const dataSession = useAppSelector(
    selectAncestor(path, isIElementSectionDataSession),
  );

  const visibleContextMenuActions = useAppSelector(
    selectVisibleContextMenuActions(dataSession?.id, false),
    shallowEqual,
  );

  if (!dataSession || !visibleContextMenuActions.length) return null;

  return (
    <AppAwareHtml
      position={position}
      calculatePosition={roundedCalculatePosition}
      style={{ pointerEvents: "none" }}
    >
      <div ref={setAnchorEl} />
      {anchorEl && (
        <SceneContextMenuBase
          aria-label="path context menu"
          anchorEl={anchorEl}
          onClose={onClose}
          sx={{
            // We need a fixed width here, to make sure that the various input components don't scale infinitely
            width: 250,
          }}
        >
          {visibleContextMenuActions.map((action, index) => {
            switch (action) {
              case ACTIONS.editName:
                return (
                  <EditNameAction
                    key={index}
                    elementId={dataSession.id}
                    dark
                    closeContextMenu={onClose}
                  />
                );
              case ACTIONS.adjustTrajectory:
                return (
                  <AdjustTrajectoryAction
                    key={index}
                    elementId={dataSession.id}
                    dark
                  />
                );
              case ACTIONS.moveDataSession:
                return (
                  <MoveDataSessionAction
                    key={index}
                    elementId={dataSession.id}
                    closeContextMenu={onClose}
                    dark
                  />
                );
              case ACTIONS.deleteVideoRecording:
                return (
                  <DeleteVideoRecordingAction
                    key={index}
                    elementId={dataSession.id}
                    closeContextMenu={onClose}
                    dark
                  />
                );
              case ACTIONS.downloadVideoWalk:
                return (
                  <DownloadVideoWalkAction
                    key={index}
                    elementId={dataSession.id}
                    closeContextMenu={onClose}
                    dark
                  />
                );
              case MenuDivider:
                return <FaroMenuSeparator key={index} dark />;
              default:
                return null;
            }
          })}
        </SceneContextMenuBase>
      )}
    </AppAwareHtml>
  );
}
